// src/pages/admin/AdminSettings.js
// Admin : Paramètres du site (identité, apparence, commandes, paiement, support) — whitelabel
// Maxula

import React, { useEffect, useState, useRef } from 'react';
import {
  FiUpload, FiSave, FiSettings, FiImage, FiShoppingBag, FiCreditCard,
  FiLifeBuoy, FiSearch, FiDroplet, FiClock, FiPercent, FiGlobe, FiTrendingUp,
  FiRefreshCw, FiAlertTriangle, FiStar, FiCalendar, FiTrash2, FiPlus, FiMapPin, FiAward
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API, { settingsAPI } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { normalizeImagePath } from '../../utils/imageUtils';

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400';

const TABS = [
  { key: 'general', label: 'Général', icon: FiSettings },
  { key: 'apparence', label: 'Apparence', icon: FiImage },
  { key: 'commandes', label: 'Commandes', icon: FiShoppingBag },
  { key: 'paiement', label: 'Paiement', icon: FiCreditCard },
  { key: 'support', label: 'Support', icon: FiLifeBuoy },
  { key: 'tracking', label: 'Marketing', icon: FiTrendingUp },
  { key: 'rotation', label: 'Rotation accueil', icon: FiRefreshCw },
  { key: 'payouts', label: 'Paiements partenaires', icon: FiCalendar },
  { key: 'fidelite', label: 'Fidélité', icon: FiAward },
];

// Carte de section : en-tête (icône + titre + description) puis contenu.
const Card = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100">
      <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <h2 className="font-semibold text-gray-900 leading-tight">{title}</h2>
        {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// Champ : label + contenu + indication.
const Field = ({ label, hint, children, className = '' }) => (
  <div className={className}>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const AdminSettings = () => {
  const { refresh } = useSettings() || {};
  const [tab, setTab] = useState('general');
  const [form, setForm] = useState({
    siteName: '', primaryColor: '#E11D79', secondaryColor: '#D0176A',
    heroTitle: '', heroSubtitle: '',
    metaTitle: '', metaDescription: '', metaKeywords: '',
    support_whatsapp: '', support_phone: '', support_email: '',
    bank_accountHolder: '', bank_bankName: '', bank_rib: '', bank_iban: '', bank_swift: '', bank_notes: '',
    refundWindowDays: 14, platformTvaRate: 0, orderAutoConfirmDays: 7, digitalTransferWindowDays: 30,
    payzoneCashMinAmount: 0,
    metaPixelId: '', googleAnalyticsId: '', tiktokPixelId: '', trackingEnabled: true,
    featuredRotationEnabled: true, featuredRotationMinutes: 60, featuredRotationSize: 3,
    // v1.419.0 — paiements partenaires
    payoutBusinessDays: 20,
    payout_bank_transfer: true, payout_cash: true, payout_cheque: true,
    pickup_address: '', pickup_hours: '', pickup_phone: '',
    // v1.467.0 — fidélité
    loyaltyEnabled: true, loyaltyMadPerPoint: 10
  });
  // Paliers : [{ points, label, message }]
  const [paliers, setPaliers] = useState([]);
  const [nouveauPalier, setNouveauPalier] = useState({ points: '', label: '', message: '' });
  // Jours fériés saisis par l'administration : [{ date, label }]
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: '', label: '' });
  // Aperçu de la rotation (planning des prochains créneaux).
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const heroRef = useRef();
  const [logoFile, setLogoFile] = useState(null);
  const [sidebarFile, setSidebarFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [sidebarPreview, setSidebarPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(); const sbRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.get();
        const s = res.data?.data || {};
        setForm({
          siteName: s.siteName || '',
          primaryColor: s.primaryColor || '#E11D79',
          secondaryColor: s.secondaryColor || '#D0176A',
          heroTitle: s.heroTitle || '',
          heroSubtitle: s.heroSubtitle || '',
          metaTitle: s.metaTitle || '',
          metaDescription: s.metaDescription || '',
          metaKeywords: s.metaKeywords || '',
          support_whatsapp: s.support?.whatsapp || '',
          support_phone: s.support?.phone || '',
          support_email: s.support?.email || '',
          bank_accountHolder: s.bankTransfer?.accountHolder || '',
          bank_bankName: s.bankTransfer?.bankName || '',
          bank_rib: s.bankTransfer?.rib || '',
          refundWindowDays: Number(s.refundWindowDays) || 14,
          orderAutoConfirmDays: Number(s.orderAutoConfirmDays) || 7,
          digitalTransferWindowDays: Number(s.digitalTransferWindowDays) || 30,
          platformTvaRate: Number(s.platformTvaRate) || 0,
          payzoneCashMinAmount: Number(s.payzoneCashMinAmount) || 0,
          metaPixelId: s.metaPixelId || '',
          googleAnalyticsId: s.googleAnalyticsId || '',
          tiktokPixelId: s.tiktokPixelId || '',
          trackingEnabled: s.trackingEnabled !== false,
          featuredRotationEnabled: s.featuredRotationEnabled !== false,
          featuredRotationMinutes: Number(s.featuredRotationMinutes) || 60,
          featuredRotationSize: Number(s.featuredRotationSize) || 3,
          payoutBusinessDays: Number.isFinite(Number(s.payoutBusinessDays)) ? Number(s.payoutBusinessDays) : 20,
          payout_bank_transfer: s.payoutMethodsEnabled?.bank_transfer !== false,
          payout_cash: s.payoutMethodsEnabled?.cash !== false,
          payout_cheque: s.payoutMethodsEnabled?.cheque !== false,
          pickup_address: s.payoutPickupInfo?.address || '',
          pickup_hours: s.payoutPickupInfo?.hours || '',
          pickup_phone: s.payoutPickupInfo?.phone || '',
          loyaltyEnabled: s.loyaltyEnabled !== false,
          loyaltyMadPerPoint: Number(s.loyaltyMadPerPoint) || 10,
          bank_iban: s.bankTransfer?.iban || '',
          bank_swift: s.bankTransfer?.swift || '',
          bank_notes: s.bankTransfer?.notes || ''
        });
        setHolidays(Array.isArray(s.publicHolidays) ? s.publicHolidays : []);
        setPaliers(Array.isArray(s.loyaltyTiers) ? s.loyaltyTiers : []);
        if (s.logo) setLogoPreview(normalizeImagePath(s.logo));
        if (s.sidebarImage) setSidebarPreview(normalizeImagePath(s.sidebarImage));
        if (s.heroImage) setHeroPreview(normalizeImagePath(s.heroImage));
      } catch (e) { toast.error('Erreur de chargement'); }
      finally { setLoading(false); }
    })();
  }, []);

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Aperçu : on interroge le serveur avec les valeurs AFFICHÉES (pas encore
  // enregistrées), pour que l'admin puisse essayer une durée avant de valider.
  const loadPreview = async () => {
    try {
      setPreviewLoading(true);
      const res = await API.get('/admin/featured-rotation/preview', {
        params: {
          window: form.featuredRotationMinutes,
          limit: form.featuredRotationSize,
          slots: 6
        }
      });
      setPreview(res.data || null);
    } catch (e) {
      toast.error("Aperçu indisponible");
      setPreview(null);
    } finally { setPreviewLoading(false); }
  };

  // --- Jours fériés ---------------------------------------------------------
  const addHoliday = () => {
    const d = String(newHoliday.date || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return toast.error('Choisissez une date.');
    // Un doublon ferait compter le même jour deux fois dans le calcul.
    if (holidays.some((h) => h.date === d)) return toast.error('Cette date est déjà dans la liste.');
    const next = [...holidays, { date: d, label: (newHoliday.label || '').trim() || 'Jour férié' }];
    next.sort((a, b) => a.date.localeCompare(b.date));
    setHolidays(next);
    setNewHoliday({ date: '', label: '' });
  };
  const removeHoliday = (d) => setHolidays((prev) => prev.filter((h) => h.date !== d));

  // --- Paliers de fidélité ---
  const addPalier = () => {
    const pts = parseInt(nouveauPalier.points, 10);
    const label = String(nouveauPalier.label || '').trim();
    if (!Number.isFinite(pts) || pts < 1) return toast.error('Indiquez un nombre de points.');
    // Un palier sans intitulé ne veut rien dire pour le client : on refuse
    // plutôt que d'envoyer « vous avez atteint le palier 3 ».
    if (!label) return toast.error('Indiquez ce que le client obtient.');
    if (paliers.some((p) => Number(p.points) === pts)) return toast.error('Ce seuil existe déjà.');
    const next = [...paliers, { points: pts, label, message: String(nouveauPalier.message || '').trim() }];
    next.sort((a, b) => a.points - b.points);
    setPaliers(next);
    setNouveauPalier({ points: '', label: '', message: '' });
  };
  const removePalier = (pts) => setPaliers((prev) => prev.filter((p) => Number(p.points) !== Number(pts)));
  const pickLogo = (e) => { const f = e.target.files?.[0]; if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); } };
  const pickSb = (e) => { const f = e.target.files?.[0]; if (f) { setSidebarFile(f); setSidebarPreview(URL.createObjectURL(f)); } };
  const pickHero = (e) => { const f = e.target.files?.[0]; if (f) { setHeroFile(f); setHeroPreview(URL.createObjectURL(f)); } };

  const save = async () => {
    try {
      setSaving(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      // v1.419.0 — objets/tableaux : FormData ne transporte que du texte, donc
      // on sérialise. Sans JSON.stringify, un objet arriverait au serveur sous
      // la forme littérale « [object Object] » et le réglage serait perdu.
      fd.append('payoutMethodsEnabled', JSON.stringify({
        bank_transfer: !!form.payout_bank_transfer,
        cash: !!form.payout_cash,
        cheque: !!form.payout_cheque
      }));
      fd.append('publicHolidays', JSON.stringify(holidays));
      fd.append('loyaltyTiers', JSON.stringify(paliers));
      fd.append('payoutPickupInfo', JSON.stringify({
        address: form.pickup_address, hours: form.pickup_hours, phone: form.pickup_phone
      }));
      if (logoFile) fd.append('logo', logoFile);
      if (sidebarFile) fd.append('sidebarImage', sidebarFile);
      if (heroFile) fd.append('heroImage', heroFile);
      await settingsAPI.update(fd);
      toast.success('Paramètres enregistrés');
      if (typeof refresh === 'function') refresh();
    } catch (e) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;

  return (
    <div className="pb-24">
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Paramètres du site</h1>
        <p className="text-sm text-gray-500 mb-5">Configurez l'identité, l'apparence et les règles de la plateforme.</p>

        {/* Onglets */}
        <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition ${active ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                <t.icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6 space-y-6">
          {/* ===================== GÉNÉRAL ===================== */}
          {tab === 'general' && (
            <>
              <Card icon={FiGlobe} title="Identité" desc="Nom et logos de la plateforme.">
                <Field label="Nom du site" className="mb-5">
                  <input name="siteName" value={form.siteName} onChange={onField} className={inputCls} />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Logo">
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-20 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
                        {logoPreview ? <img src={logoPreview} alt="logo" className="w-full h-full object-contain" /> : <span className="text-gray-300 text-xs">Logo</span>}
                      </div>
                      <button type="button" onClick={() => logoRef.current?.click()} className="px-3 py-2 rounded-lg border border-gray-300 text-sm flex items-center gap-1.5 hover:bg-gray-50"><FiUpload size={14} /> Choisir</button>
                      <input ref={logoRef} type="file" accept="image/*" onChange={pickLogo} className="hidden" />
                    </div>
                  </Field>
                  <Field label="Image sidebar">
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-20 rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center">
                        {sidebarPreview ? <img src={sidebarPreview} alt="sidebar" className="w-full h-full object-cover" /> : <span className="text-gray-300 text-xs">Image</span>}
                      </div>
                      <button type="button" onClick={() => sbRef.current?.click()} className="px-3 py-2 rounded-lg border border-gray-300 text-sm flex items-center gap-1.5 hover:bg-gray-50"><FiUpload size={14} /> Choisir</button>
                      <input ref={sbRef} type="file" accept="image/*" onChange={pickSb} className="hidden" />
                    </div>
                  </Field>
                </div>
              </Card>

              <Card icon={FiSearch} title="SEO (référencement)" desc="Métadonnées par défaut du site.">
                <Field label="Titre meta (balise title par défaut)" hint="Recommandé : 50–60 caractères." className="mb-4">
                  <input name="metaTitle" value={form.metaTitle} onChange={onField} placeholder="Maxula — Les meilleurs deals au Maroc" className={inputCls} />
                </Field>
                <Field label="Description meta" hint="Recommandé : 150–160 caractères." className="mb-4">
                  <textarea name="metaDescription" value={form.metaDescription} onChange={onField} rows={2} placeholder="Découvrez les meilleures offres et promotions près de chez vous…" className={inputCls} />
                </Field>
                <Field label="Mots-clés (séparés par des virgules)" className="mb-4">
                  <input name="metaKeywords" value={form.metaKeywords} onChange={onField} placeholder="deals maroc, promotions, bons plans, restaurants, spa" className={inputCls} />
                </Field>
                <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                  <strong>sitemap.xml</strong> et <strong>robots.txt</strong> sont générés automatiquement par le serveur (racine du domaine). Soumettez <code>/sitemap.xml</code> dans Google Search Console.
                </div>
              </Card>
            </>
          )}

          {/* ===================== APPARENCE ===================== */}
          {tab === 'apparence' && (
            <>
              <Card icon={FiImage} title="Bannière d'accueil (Hero)" desc="Le grand visuel en haut de la page d'accueil.">
                <Field label="Titre" className="mb-3">
                  <input name="heroTitle" value={form.heroTitle} onChange={onField} placeholder="Découvrez les meilleurs deals au Maroc" className={inputCls} />
                </Field>
                <Field label="Sous-titre" className="mb-3">
                  <input name="heroSubtitle" value={form.heroSubtitle} onChange={onField} placeholder="Économisez jusqu'à 90%..." className={inputCls} />
                </Field>
                <Field label="Image de fond">
                  <p className="text-xs text-gray-500 mb-2">
                    Format recommandé : <strong>1920 × 800 px</strong> (ratio ~2.4:1, paysage). Min. 1600 px de large. JPG/WebP, &lt; 500 Ko.
                    L'image est recadrée (cover) et ancrée du côté image (à droite en FR, à gauche en AR) — placez le sujet principal de ce côté.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-56 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center" style={{ aspectRatio: '2.4 / 1' }}>
                      {heroPreview ? <img src={heroPreview} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-full h-full object-cover" /> : <span className="text-gray-300 text-sm">Aperçu (cadre réel)</span>}
                    </div>
                    <button type="button" onClick={() => heroRef.current?.click()} className="px-3 py-2 rounded-lg border border-gray-300 text-sm flex items-center gap-1.5 hover:bg-gray-50"><FiUpload size={14} /> Choisir</button>
                    <input ref={heroRef} type="file" accept="image/*" onChange={pickHero} className="hidden" />
                  </div>
                </Field>
              </Card>

              <Card icon={FiDroplet} title="Couleurs (thème)" desc="Appliquées aux boutons et liens principaux du site.">
                <div className="flex gap-8">
                  <Field label="Couleur principale">
                    <input type="color" name="primaryColor" value={form.primaryColor} onChange={onField} className="h-10 w-20 border border-gray-300 rounded cursor-pointer" />
                  </Field>
                  <Field label="Couleur secondaire (hover)">
                    <input type="color" name="secondaryColor" value={form.secondaryColor} onChange={onField} className="h-10 w-20 border border-gray-300 rounded cursor-pointer" />
                  </Field>
                </div>
              </Card>
            </>
          )}

          {/* ===================== COMMANDES ===================== */}
          {tab === 'commandes' && (
            <Card icon={FiClock} title="Délais & fenêtres" desc="Règles de temps appliquées à toutes les commandes.">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label="Remboursement (jours)" hint="Fenêtre pour demander un remboursement après la commande.">
                  <input type="number" min="1" max="365" value={form.refundWindowDays}
                    onChange={(e) => setForm((f) => ({ ...f, refundWindowDays: e.target.value }))} className={inputCls} />
                </Field>
                <Field label="Auto-confirmation (jours)" hint="Réception confirmée automatiquement après livraison si l'acheteur n'agit pas (relance à J-2).">
                  <input type="number" min="1" max="365" value={form.orderAutoConfirmDays}
                    onChange={(e) => setForm((f) => ({ ...f, orderAutoConfirmDays: e.target.value }))} className={inputCls} />
                </Field>
                <Field label="Traçabilité digitale (jours)" hint="Durée où l'accès à un produit digital reste tracé et un échange reste possible. Réglage global (plus défini par le marchand).">
                  <input type="number" min="1" max="365" value={form.digitalTransferWindowDays}
                    onChange={(e) => setForm((f) => ({ ...f, digitalTransferWindowDays: e.target.value }))} className={inputCls} />
                </Field>
              </div>
            </Card>
          )}

          {/* ===================== PAIEMENT ===================== */}
          {tab === 'paiement' && (
            <>
              <Card icon={FiPercent} title="TVA Maxula (abonnements)" desc="TVA appliquée aux factures d'abonnement marchand.">
                <Field label="Taux TVA abonnements (%)" hint="Laissez 0 si Maxula n'est pas assujettie à la TVA. La TVA est décomposée du total, jamais ajoutée par-dessus." className="max-w-xs">
                  <input type="number" min="0" max="100" step="1" name="platformTvaRate" value={form.platformTvaRate} onChange={onField} className={inputCls} />
                </Field>
              </Card>

              <Card icon={FiShoppingBag} title="Paiement en espèces en agence (PayExpress)" desc="Option « espèces en agence » proposée au checkout. Côté client, le service est présenté sous le nom PAYEXPRESS : c'est l'enseigne que les agents connaissent au guichet. Payzone reste la passerelle technique.">
                <Field label="Montant minimum (MAD)" hint="En dessous de ce montant, l'option « espèces en agence » est masquée au checkout. Laissez 0 pour aucun plancher. Payzone applique aussi son propre minimum." className="max-w-xs">
                  <input type="number" min="0" step="1" name="payzoneCashMinAmount" value={form.payzoneCashMinAmount} onChange={onField} className={inputCls} />
                </Field>
              </Card>

              <Card icon={FiCreditCard} title="Coordonnées bancaires (virement)" desc="Affichées au marchand/agence qui choisit « Virement bancaire ».">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Titulaire du compte"><input name="bank_accountHolder" value={form.bank_accountHolder} onChange={onField} placeholder="Maxula" className={inputCls} /></Field>
                  <Field label="Banque"><input name="bank_bankName" value={form.bank_bankName} onChange={onField} placeholder="Ex : Attijariwafa Bank" className={inputCls} /></Field>
                  <Field label="RIB"><input name="bank_rib" value={form.bank_rib} onChange={onField} placeholder="24 chiffres" className={inputCls} /></Field>
                  <Field label="IBAN"><input name="bank_iban" value={form.bank_iban} onChange={onField} placeholder="MA64 ..." className={inputCls} /></Field>
                  <Field label="SWIFT / BIC"><input name="bank_swift" value={form.bank_swift} onChange={onField} placeholder="BCMAMAMC" className={inputCls} /></Field>
                  <Field label="Note (optionnel)"><input name="bank_notes" value={form.bank_notes} onChange={onField} placeholder="Ex : préciser la réf. SUB-... en motif" className={inputCls} /></Field>
                </div>
              </Card>
            </>
          )}

          {/* ===================== SUPPORT ===================== */}
          {tab === 'support' && (
            <Card icon={FiLifeBuoy} title="Support (contact affiché aux utilisateurs)" desc="Coordonnées de contact visibles sur le site et les factures.">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="WhatsApp"><input name="support_whatsapp" value={form.support_whatsapp} onChange={onField} placeholder="212600000000" className={inputCls} /></Field>
                <Field label="Téléphone"><input name="support_phone" value={form.support_phone} onChange={onField} className={inputCls} /></Field>
                <Field label="Email"><input name="support_email" value={form.support_email} onChange={onField} placeholder="contact@maxula.ma" className={inputCls} /></Field>
              </div>
            </Card>
          )}

          {tab === 'tracking' && (
            <Card icon={FiTrendingUp} title="Marketing & Tracking" desc="Pixels de mesure et de retargeting. Laissez un champ vide pour désactiver le tracker correspondant. Les scripts ne se chargent qu'après le consentement cookies du visiteur.">
              <label className="flex items-center gap-3 mb-5 cursor-pointer select-none">
                <input type="checkbox" name="trackingEnabled" checked={!!form.trackingEnabled} onChange={(e) => setForm((f) => ({ ...f, trackingEnabled: e.target.checked }))} className="w-5 h-5 rounded border-gray-300 text-pink-500 focus:ring-pink-400" />
                <span className="text-sm font-medium text-gray-800">Activer le tracking marketing (interrupteur global)</span>
              </label>

              <div className="grid grid-cols-1 gap-4">
                <Field label="Meta Pixel ID (Facebook / Instagram)">
                  <input name="metaPixelId" value={form.metaPixelId} onChange={onField} placeholder="Ex : 1234567890123456" className={inputCls} />
                </Field>
                <Field label="Google Analytics ID (GA4)">
                  <input name="googleAnalyticsId" value={form.googleAnalyticsId} onChange={onField} placeholder="Ex : G-XXXXXXXXXX" className={inputCls} />
                </Field>
                <Field label="TikTok Pixel ID">
                  <input name="tiktokPixelId" value={form.tiktokPixelId} onChange={onField} placeholder="Ex : CXXXXXXXXXXXXXXXXX" className={inputCls} />
                </Field>
              </div>

              <p className="mt-4 text-xs text-gray-500 leading-relaxed">
                Événements de conversion suivis automatiquement : consultation de page (PageView), ajout au panier (AddToCart), inscription (Registration) et achat (Purchase, avec montant). Ces événements sont envoyés vers chaque pixel actif dès qu'il est configuré.
              </p>
            </Card>
          )}

          {/* ===================== ROTATION ACCUEIL ===================== */}
          {tab === 'rotation' && (
            <Card
              icon={FiRefreshCw}
              title="Rotation des offres mises en avant"
              desc="Les offres cochées « mise en avant » (étoile, dans Gestion des annonces) défilent automatiquement sur la page d'accueil et sur le hub maxula.ma. Chaque offre passe à son tour, pour un temps d'affichage identique."
            >
              <label className="flex items-center gap-3 mb-5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="featuredRotationEnabled"
                  checked={!!form.featuredRotationEnabled}
                  onChange={(e) => setForm((f) => ({ ...f, featuredRotationEnabled: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-pink-500 focus:ring-pink-400"
                />
                <span className="text-sm font-medium text-gray-800">Activer la rotation automatique</span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Durée d'affichage d'un lot"
                  hint="En minutes. 60 = les offres changent toutes les heures, pile à l'heure ronde."
                >
                  <select
                    name="featuredRotationMinutes"
                    value={form.featuredRotationMinutes}
                    onChange={onField}
                    className={inputCls}
                  >
                    <option value={5}>5 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 heure</option>
                    <option value={120}>2 heures</option>
                    <option value={360}>6 heures</option>
                    <option value={720}>12 heures</option>
                    <option value={1440}>24 heures</option>
                  </select>
                </Field>

                <Field
                  label="Nombre d'offres affichées à la fois"
                  hint="Le hub maxula.ma en attend 3. Il faut au moins une offre de plus que ce nombre pour que la rotation change quelque chose."
                >
                  <input
                    type="number" min={1} max={24}
                    name="featuredRotationSize"
                    value={form.featuredRotationSize}
                    onChange={onField}
                    className={inputCls}
                  />
                </Field>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={loadPreview}
                  disabled={previewLoading}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <FiRefreshCw className={previewLoading ? 'animate-spin' : ''} size={15} />
                  {previewLoading ? 'Calcul…' : "Afficher l'aperçu du planning"}
                </button>
                <span className="text-xs text-gray-500">Utilise les valeurs ci-dessus, même non enregistrées.</span>
              </div>

              {preview && (
                <div className="mt-5">
                  {/* Diagnostic : c'est ce qu'il faut lire quand « ça ne tourne pas ». */}
                  {preview.hint && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 mb-4">
                      <FiAlertTriangle className="text-amber-500 mt-0.5 shrink-0" size={16} />
                      <p className="text-sm text-amber-800">{preview.hint}</p>
                    </div>
                  )}
                  {preview.rotation?.source === 'fallback-active' && (
                    <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 mb-4">
                      <FiStar className="text-blue-500 mt-0.5 shrink-0" size={16} />
                      <p className="text-sm text-blue-800">
                        Aucune offre n'est cochée « mise en avant » : la page d'accueil fait tourner
                        les offres actives les plus récentes. Cochez l'étoile sur vos offres dans
                        Gestion des annonces pour maîtriser cette sélection.
                      </p>
                    </div>
                  )}

                  <div className="text-sm text-gray-600 mb-3">
                    <strong>{preview.poolSize}</strong> offre(s) dans la rotation ·
                    un tour complet dure <strong>{preview.rotation?.cycleSlots}</strong> créneau(x)
                    {preview.rotation?.windowMinutes ? ` de ${preview.rotation.windowMinutes} min` : ''}.
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium whitespace-nowrap">Créneau</th>
                          <th className="text-left px-4 py-2 font-medium">Offres affichées</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(preview.schedule || []).map((row, i) => (
                          <tr key={row.from} className={i === 0 ? 'bg-primary-50/40' : ''}>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                              {new Date(row.from).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                              {i === 0 && <span className="ml-2 text-xs font-semibold text-primary-600">en cours</span>}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {row.deals.map((d) => d.title).join(' · ') || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* ===================== PAIEMENTS PARTENAIRES ===================== */}
          {tab === 'payouts' && (
            <>
              <Card
                icon={FiCalendar}
                title="Délai et modes de règlement"
                desc="Quand un partenaire demande son paiement, le système calcule automatiquement la date prévue en jours ouvrés, week-ends et jours fériés exclus. Cette date est figée à la demande : elle ne bougera plus, même si vous modifiez ces réglages ensuite."
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  <Field
                    label="Délai de paiement (jours ouvrés)"
                    hint="20 = le partenaire est réglé 20 jours ouvrés après sa demande."
                  >
                    <input type="number" min={0} max={180} name="payoutBusinessDays"
                      value={form.payoutBusinessDays} onChange={onField} className={inputCls} />
                  </Field>
                </div>

                <div className="text-sm font-medium text-gray-700 mb-2">Modes proposés aux partenaires</div>
                <div className="space-y-2">
                  {[
                    ['payout_bank_transfer', 'Virement bancaire / RIB', 'Nécessite le RIB du partenaire.'],
                    ['payout_cash', 'Espèces', 'Le partenaire vient retirer son règlement chez Maxula.'],
                    ['payout_cheque', 'Chèque', 'Le partenaire vient retirer son chèque chez Maxula.']
                  ].map(([key, label, hint]) => (
                    <label key={key} className="flex items-start gap-3 cursor-pointer select-none">
                      <input type="checkbox" checked={!!form[key]}
                        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
                        className="w-5 h-5 mt-0.5 rounded border-gray-300 text-pink-500 focus:ring-pink-400" />
                      <span>
                        <span className="text-sm font-medium text-gray-800">{label}</span>
                        <span className="block text-xs text-gray-400">{hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
                {/* Avertissement immédiat : le serveur refuse la sauvegarde,
                    autant le dire avant de cliquer sur Enregistrer. */}
                {!form.payout_bank_transfer && !form.payout_cash && !form.payout_cheque && (
                  <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
                    <FiAlertTriangle className="text-red-500 mt-0.5 shrink-0" size={16} />
                    <p className="text-sm text-red-800">
                      Gardez au moins un mode actif : sinon aucun partenaire ne pourra demander son paiement.
                    </p>
                  </div>
                )}
              </Card>

              <Card
                icon={FiMapPin}
                title="Guichet de retrait"
                desc="Affiché au partenaire qui choisit un règlement en espèces ou par chèque, pour qu'il sache où et quand se présenter."
              >
                <div className="grid grid-cols-1 gap-4">
                  <Field label="Adresse">
                    <input name="pickup_address" value={form.pickup_address} onChange={onField}
                      placeholder="Ex : 12 avenue Mohammed V, Tanger" className={inputCls} />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Horaires">
                      <input name="pickup_hours" value={form.pickup_hours} onChange={onField}
                        placeholder="Ex : du lundi au vendredi, 9h–17h" className={inputCls} />
                    </Field>
                    <Field label="Téléphone">
                      <input name="pickup_phone" value={form.pickup_phone} onChange={onField}
                        placeholder="Ex : 05 39 00 00 00" className={inputCls} />
                    </Field>
                  </div>
                </div>
              </Card>

              <Card
                icon={FiCalendar}
                title="Jours fériés"
                desc="Ces jours sont exclus du calcul des jours ouvrés."
              >
                {/* Point important : expliquer POURQUOI il doit saisir des
                    dates lui-même, sinon la section paraît être une corvée
                    inutile et restera vide — et les dates de paiement seront
                    fausses pendant l'Aïd. */}
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 mb-5 text-sm text-blue-900 leading-relaxed">
                  Les <strong>fêtes nationales</strong> (Fête du Trône, Marche Verte, Fête de l'Indépendance…)
                  sont déjà connues du système : leurs dates ne changent jamais, rien à saisir.
                  <div className="mt-1.5">
                    En revanche, les <strong>fêtes religieuses</strong> (Aïd al-Fitr, Aïd al-Adha, 1<sup>er</sup> Moharram,
                    Aïd al-Mawlid) dépendent de l'observation lunaire annoncée chaque année par le ministère des Habous.
                    Aucun calcul ne peut les deviner à l'avance : <strong>ajoutez-les ici dès qu'elles sont annoncées</strong>,
                    sinon les dates de paiement promises aux partenaires tomberont un jour férié.
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <input type="date" value={newHoliday.date}
                    onChange={(e) => setNewHoliday((h) => ({ ...h, date: e.target.value }))}
                    className={`${inputCls} sm:w-52`} />
                  <input type="text" value={newHoliday.label} maxLength={80}
                    onChange={(e) => setNewHoliday((h) => ({ ...h, label: e.target.value }))}
                    placeholder="Libellé (ex : Aïd al-Fitr)" className={`${inputCls} flex-1`} />
                  <button type="button" onClick={addHoliday}
                    className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 inline-flex items-center justify-center gap-2 shrink-0">
                    <FiPlus size={15} /> Ajouter
                  </button>
                </div>

                {holidays.length === 0 ? (
                  <div className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-lg">
                    Aucun jour férié saisi.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                    {holidays.map((h) => (
                      <div key={h.date} className="flex items-center justify-between px-4 py-2.5 bg-white">
                        <div className="text-sm">
                          <span className="font-medium text-gray-800">
                            {new Date(`${h.date}T12:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                          </span>
                          <span className="text-gray-500"> — {h.label}</span>
                        </div>
                        <button type="button" onClick={() => removeHoliday(h.date)}
                          className="text-gray-300 hover:text-red-500" title="Retirer">
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-3">
                  N'oubliez pas d'enregistrer les paramètres pour appliquer vos modifications.
                </p>
              </Card>
            </>
          )}

          {/* ===================== FIDELITE ===================== */}
          {tab === 'fidelite' && (
            <>
              <Card
                icon={FiAward}
                title="Barème des points"
                desc="Les points sont un indicateur : atteindre un palier ne déclenche rien automatiquement. C'est vous qui décidez d'accorder la remise, et à qui."
              >
                <label className="flex items-center gap-3 mb-5 cursor-pointer select-none">
                  <input type="checkbox" checked={!!form.loyaltyEnabled}
                    onChange={(e) => setForm((f) => ({ ...f, loyaltyEnabled: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-300 text-pink-500 focus:ring-pink-400" />
                  <span className="text-sm font-medium text-gray-800">Attribuer des points aux clients</span>
                </label>

                <Field
                  label="Dirhams encaissés pour 1 point"
                  hint="10 = un client qui dépense 850 MAD obtient 85 points. Seules les commandes payées comptent."
                >
                  <input type="number" min={1} max={10000} name="loyaltyMadPerPoint"
                    value={form.loyaltyMadPerPoint} onChange={onField} className={`${inputCls} sm:w-40`} />
                </Field>

                {/* Conséquence immédiate, chiffrée : un réglage dont on ne voit
                    pas l'effet se règle au hasard. */}
                <div className="mt-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
                  Avec ce réglage, une commande de <strong>1 000 MAD</strong> rapporte{' '}
                  <strong>{Math.floor(1000 / (Number(form.loyaltyMadPerPoint) || 10))} points</strong>.
                </div>

                <p className="text-xs text-gray-400 mt-3">
                  Modifier ce barème ne recalcule pas l&apos;historique. Pour l&apos;appliquer aux anciennes
                  commandes, relancez <code>clients</code> dans cPanel.
                </p>
              </Card>

              <Card
                icon={FiStar}
                title="Paliers de récompense"
                desc="Ce que vous proposez à partir d'un certain nombre de points. Le message sera pré-rempli au moment de l'envoi, mais reste modifiable."
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <input type="number" min={1} value={nouveauPalier.points}
                    onChange={(e) => setNouveauPalier((p) => ({ ...p, points: e.target.value }))}
                    placeholder="Points (ex : 100)" className={inputCls} />
                  <input type="text" value={nouveauPalier.label} maxLength={160}
                    onChange={(e) => setNouveauPalier((p) => ({ ...p, label: e.target.value }))}
                    placeholder="Récompense (ex : 10% de remise)" className={`${inputCls} sm:col-span-2`} />
                </div>
                <textarea rows={2} value={nouveauPalier.message} maxLength={600}
                  onChange={(e) => setNouveauPalier((p) => ({ ...p, message: e.target.value }))}
                  placeholder="Message au client (ex : Bonjour {nom}, merci de votre fidélité ! Vous bénéficiez de 10% sur votre prochaine réservation.)"
                  className={`${inputCls} mb-3`} />
                <button type="button" onClick={addPalier}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 inline-flex items-center gap-2">
                  <FiPlus size={15} /> Ajouter ce palier
                </button>

                <p className="text-xs text-gray-400 mt-2">
                  Dans le message, <code>{'{nom}'}</code> est remplacé par le nom du client
                  et <code>{'{points}'}</code> par son solde.
                </p>

                {paliers.length === 0 ? (
                  <div className="mt-5 text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-lg">
                    Aucun palier défini. Les points s&apos;accumulent quand même : vous pourrez
                    accorder une remise au cas par cas.
                  </div>
                ) : (
                  <div className="mt-5 divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                    {paliers.map((p) => (
                      <div key={p.points} className="flex items-start justify-between px-4 py-3 bg-white gap-3">
                        <div className="min-w-0">
                          <div className="text-sm">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold mr-2">
                              <FiAward size={11} /> {p.points} pts
                            </span>
                            <span className="font-medium text-gray-800">{p.label}</span>
                          </div>
                          {p.message && <div className="text-xs text-gray-500 mt-1 break-words">{p.message}</div>}
                        </div>
                        <button type="button" onClick={() => removePalier(p.points)}
                          className="text-gray-300 hover:text-red-500 shrink-0" title="Retirer">
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Barre de sauvegarde collante */}
      <div className="sticky bottom-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <span className="text-sm text-gray-500 hidden sm:block">Les modifications s'appliquent à tout le site après enregistrement.</span>
          <button onClick={save} disabled={saving}
            className="px-6 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2 disabled:opacity-60 shrink-0">
            <FiSave /> {saving ? 'Enregistrement…' : 'Enregistrer les paramètres'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
